import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { GetPresignedDownloadURLForStatsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getPresignedDownloadUrlForStatsRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useGetPresignedDownloadURLForStatsRPCClientImpl } from "../rpcs/RPC";
import { downloadFile } from "../../utils/FileDownloadUtils";

const DEFAULT_PROJECT_FILE_NAME = "project";

const getStatsFileName = (
  projectName: string,
  statType: GetPresignedDownloadURLForStatsRPC.RequestEnums.StatType.StatType,
): string => {
  const sanitizedProjectName =
    projectName.trim().toLocaleLowerCase().replace(/\s+/g, "-") ||
    DEFAULT_PROJECT_FILE_NAME;

  switch (statType) {
    case GetPresignedDownloadURLForStatsRPC.RequestEnums.StatType.StatType
      .UNSENT:
      return `${sanitizedProjectName}-unsent.csv`;
    case GetPresignedDownloadURLForStatsRPC.RequestEnums.StatType.StatType
      .OPT_OUT:
      return `${sanitizedProjectName}-opt-outs.csv`;
    case GetPresignedDownloadURLForStatsRPC.RequestEnums.StatType.StatType
      .BOUNCES_AND_COMPLAINTS:
      return `${sanitizedProjectName}-bounces-and-complaints.csv`;
    default:
      statType satisfies never;
      return `${sanitizedProjectName}.csv`;
  }
};

const getInitialState = (): SnapshotIn<
  typeof DownloadStatsForProjectStore
> => ({
  downloadingStatType: null,
  getPresignedDownloadURLForStatsRPCError: null,
  hasUnexpectedDownloadError: false,
});

export const DownloadStatsForProjectStore = types
  .model("DownloadStatsForProjectStore", {
    downloadingStatType: types.maybeNull(
      types.enumeration(
        "DownloadStatsForProjectStatType",
        Object.values(
          GetPresignedDownloadURLForStatsRPC.RequestEnums.StatType.StatType,
        ),
      ),
    ),
    getPresignedDownloadURLForStatsRPCError: types.maybeNull(
      types.enumeration(
        "GetPresignedDownloadURLForStatsRPCError",
        Object.values(GetPresignedDownloadURLForStatsRPC.RPCError),
      ),
    ),
    hasUnexpectedDownloadError: types.boolean,
  })
  .actions((store) => ({
    downloadStatsForProject: flow(function* (
      projectId: string,
      projectName: string,
      statType: GetPresignedDownloadURLForStatsRPC.RequestEnums.StatType.StatType,
    ) {
      if (store.downloadingStatType !== null) {
        return;
      }

      store.downloadingStatType = statType;
      store.getPresignedDownloadURLForStatsRPCError = null;
      store.hasUnexpectedDownloadError = false;

      try {
        const apiClient = getAPIClient(store);
        const request = new GetPresignedDownloadURLForStatsRPC.Request(
          new LeoUUID(projectId),
          statType,
        );

        const result: LeoRPCResult<
          GetPresignedDownloadURLForStatsRPC.Response,
          GetPresignedDownloadURLForStatsRPC.Errors.Errors
        > =
          yield useGetPresignedDownloadURLForStatsRPCClientImpl(
            apiClient,
          ).execute(request);

        if ("response" in result) {
          yield downloadFile(
            result.response.documentURL.href,
            getStatsFileName(projectName, statType),
          );
          return;
        }

        switch (result.error.code) {
          case GetPresignedDownloadURLForStatsRPC.RPCError.InvalidProjectId:
            store.getPresignedDownloadURLForStatsRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } catch {
        store.hasUnexpectedDownloadError = true;
      } finally {
        store.downloadingStatType = null;
      }
    }),
    setHasUnexpectedDownloadError: (
      hasUnexpectedDownloadError: boolean,
    ): void => {
      store.hasUnexpectedDownloadError = hasUnexpectedDownloadError;
    },
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createDownloadStatsForProjectStore = (): Instance<
  typeof DownloadStatsForProjectStore
> => DownloadStatsForProjectStore.create(getInitialState());
